import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Banknote } from "lucide-react";
import { toast } from "sonner";

export const fmtTL = (n) => `${Number(n || 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 })}₺`;

const nowLocal = () => new Date(Date.now() + 3 * 3600 * 1000).toISOString().slice(0, 16);

const fmtDT = (iso) => (iso.startsWith("1970") ? "Başlangıç" : new Date(iso).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short", timeZone: "Europe/Istanbul" }));

export function RevenueDialog({ product, onClose, onSaved }) {
  const [hist, setHist] = useState([]);
  const [amount, setAmount] = useState("");
  const [until, setUntil] = useState(nowLocal());

  useEffect(() => {
    if (!product) return;
    setAmount(""); setUntil(nowLocal());
    http.get(`/products/${product.id}/revenue`).then((r) => setHist(r.data));
  }, [product]);

  const save = async () => {
    if (amount === "" || Number(amount) < 0) { toast.error("Geçerli bir tutar girin"); return; }
    try {
      const { data } = await http.post(`/products/${product.id}/revenue`, {
        amount: Number(amount), old_valid_until: hist.length ? `${until}:00+03:00` : null,
      });
      setHist(data); setAmount("");
      toast.success("Ortalama gelir kaydedildi");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
  };
  const remove = async (h) => {
    if (!window.confirm(`${fmtTL(h.amount)} kaydı silinsin mi?`)) return;
    const { data } = await http.delete(`/products/${product.id}/revenue/${h.id}`);
    setHist(data); onSaved();
  };

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-md" data-testid="revenue-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Banknote size={18} className="text-emerald-400" /> Ortalama Gelir · {product?.name}</DialogTitle></DialogHeader>
        <div className="rounded-xl border border-slate-800 divide-y divide-slate-800" data-testid="revenue-history">
          {[...hist].reverse().map((h, i) => (
            <div key={h.id} className="flex items-center gap-3 px-3 py-2 text-sm">
              <span className={`font-display font-bold ${i === 0 ? "text-emerald-300" : "text-slate-400"}`}>{fmtTL(h.amount)}</span>
              <span className="text-xs text-slate-500 flex-1">{fmtDT(h.valid_from)} itibarıyla{i === 0 ? " · güncel" : ""}</span>
              <Button size="sm" variant="ghost" onClick={() => remove(h)} data-testid={`revenue-delete-${h.id}`}><Trash2 size={13} className="text-red-400" /></Button>
            </div>
          ))}
          {hist.length === 0 && <div className="px-3 py-4 text-xs text-slate-500">Henüz gelir girilmemiş. İlk girdiğiniz tutar tüm satışlara uygulanır.</div>}
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">{hist.length ? "Yeni Ortalama Gelir (₺)" : "Ortalama Gelir (₺)"}</Label>
            <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="bg-slate-950/50 border-slate-700 mt-1" data-testid="revenue-amount" />
          </div>
          {hist.length > 0 && (
            <div>
              <Label className="text-xs">Eski gelir şu tarih-saate kadar geçerli (Türkiye saati)</Label>
              <Input type="datetime-local" value={until} onChange={(e) => setUntil(e.target.value)} className="bg-slate-950/50 border-slate-700 mt-1" data-testid="revenue-until" />
              <p className="text-[11px] text-slate-500 mt-1">Bu andan sonraki satışlar yeni gelirle hesaplanır.</p>
            </div>
          )}
          <Button onClick={save} className="w-full bg-emerald-600 hover:bg-emerald-500" data-testid="revenue-save">Kaydet</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
